/**
 * Unit conversions for the values on a waste characterization.
 *
 * These live here, in code, rather than in an extraction prompt. A model does
 * `lbs/gal ÷ 8.34` unreliably and code does it exactly, and a conversion buried
 * in prose cannot be tested or reused. The extractors ask for the number and the
 * unit the sheet printed; this turns that into what the profile stores.
 */

/** The temperature scales a sheet states a flash point in. */
export const TemperatureUnit = {
  FAHRENHEIT: "FAHRENHEIT",
  CELSIUS: "CELSIUS",
} as const;

export type TemperatureUnit = (typeof TemperatureUnit)[keyof typeof TemperatureUnit];

/**
 * The ways a sheet reports how heavy the waste is.
 *
 * Specific gravity is dimensionless and relative to water; the rest are real
 * densities that have to be divided by water's value in the same units. Kept as a
 * closed set so the extractor has something definite to choose from and an
 * unrecognized unit cannot silently become a specific gravity.
 */
export const DensityUnit = {
  SPECIFIC_GRAVITY: "SPECIFIC_GRAVITY",
  GRAMS_PER_ML: "GRAMS_PER_ML",
  GRAMS_PER_CM3: "GRAMS_PER_CM3",
  KILOGRAMS_PER_LITER: "KILOGRAMS_PER_LITER",
  POUNDS_PER_GALLON: "POUNDS_PER_GALLON",
} as const;

export type DensityUnit = (typeof DensityUnit)[keyof typeof DensityUnit];

/**
 * Water's density in each unit, which is what makes a reading dimensionless.
 *
 * The metric three are 1 by definition at the temperatures these sheets care
 * about. `SPECIFIC_GRAVITY` is 1 because such a reading is already dimensionless
 * — listing it here rather than special-casing it keeps the conversion one
 * division with no branch.
 */
export const WATER_DENSITY: Record<DensityUnit, number> = {
  [DensityUnit.SPECIFIC_GRAVITY]: 1,
  [DensityUnit.GRAMS_PER_ML]: 1,
  [DensityUnit.GRAMS_PER_CM3]: 1,
  [DensityUnit.KILOGRAMS_PER_LITER]: 1,
  [DensityUnit.POUNDS_PER_GALLON]: 8.34,
};

/** Convert a temperature to Fahrenheit, which is what `flashPointF` holds. */
export function toFahrenheit(value: number, unit: TemperatureUnit): number {
  return unit === TemperatureUnit.CELSIUS ? value * (9 / 5) + 32 : value;
}

export function toCelsius(value: number, unit: TemperatureUnit): number {
  return unit === TemperatureUnit.FAHRENHEIT ? (value - 32) * (5 / 9) : value;
}

/**
 * Convert a density to a dimensionless specific gravity.
 *
 * Returns null for a non-finite reading rather than propagating a NaN into a
 * Float column, and for a non-positive one: a density of zero or less is not a
 * measurement, and dividing by water would give a value that looks real.
 */
export function toSpecificGravity(value: number, unit: DensityUnit): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  return value / WATER_DENSITY[unit];
}

/**
 * Collapse a reported density range to the single specific gravity the profile
 * stores.
 *
 * The midpoint, and deliberately not the low or high end: neither is more
 * conservative for a value that feeds a weight calculation in both directions.
 * The range the sheet stated is preserved on the extraction payload, so the
 * collapse is visible to a reviewer rather than silent — which is the part that
 * was wrong when the prompt did this.
 */
export function specificGravityFromRange(
  low: number | null,
  high: number | null,
  unit: DensityUnit
): number | null {
  const lowSg = low == null ? null : toSpecificGravity(low, unit);
  const highSg = high == null ? null : toSpecificGravity(high, unit);

  if (lowSg != null && highSg != null) return (lowSg + highSg) / 2;
  return lowSg ?? highSg;
}
